// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@werner/App/Components/Common/XngZone";
import { xProductDetailsPartNumbers } from "@werner/App/SharedStyle/ProductPartNumbersStyles";
import { DivWrapper } from "@werner/XDL/constants";
import { color, xScreen } from "@werner/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    showBrand = "showBrand",
    showMFGPartNumber = "showMFGPartNumber",
    showWernerPartNumber = "showWernerPartNumber",
    showUPC = "showUPC",
    showCustomerPartNumber = "showCustomerPartNumber",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showBrand]: boolean;
        [fields.showMFGPartNumber]: boolean;
        [fields.showWernerPartNumber]: boolean;
        [fields.showUPC]: boolean;
        [fields.showCustomerPartNumber]: boolean;
    };
}

type Props = OwnProps & HasProduct;

interface ProductDetailsPartNumbersStyles {
    container?: InjectableCss;
    label?: TypographyProps;
    value?: TypographyProps;
    brandNameLink?: LinkPresentationProps;
}

const productDetailsPartNumbersStyles: ProductDetailsPartNumbersStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-top: 12px;
            @media print {
                margin-top: 48px;
            }
        `,
    },
    label: {
        ...xProductDetailsPartNumbers.label,
    },
    value: {
        ...xProductDetailsPartNumbers.value,
    },
    brandNameLink: {
        color: "secondary",
        typographyProps: {
            ...xProductDetailsPartNumbers.value,
        },
    },
};

const styles = productDetailsPartNumbersStyles;

const ProductDetailsPartNumbers: FC<Props> = ({ id, product, fields }) => {
    if (product.isVariantParent) {
        return null;
    }
    return (
        <StyledWrapper {...styles.container} data-test-selector="ProductDetailsPartNumbers">
            {fields.showBrand && product.brand ? (
                <Typography {...styles.value} as="p">
                    <Typography {...styles.label}>{translate("Brand")}</Typography>
                    <Link
                        {...styles.brandNameLink}
                        href={product.brand?.detailPagePath}
                        data-test-selector="brandLink"
                        aria-label={`${product.brand?.name}`}
                    >
                        {product.brand?.name}
                    </Link>
                </Typography>
            ) : null}
            {fields.showMFGPartNumber
                ? createWidgetElement("ProductDetails/ProductDetailsManufacturingPartNumber", {
                      fields: {},
                  })
                : null}
            {fields.showWernerPartNumber
                ? createWidgetElement("ProductDetails/ProductDetailsPartNumber", {
                      fields: {},
                  })
                : null}
            {fields.showUPC && product.detail?.upcCode ? (
                <Typography {...styles.value} as="p">
                    <Typography {...styles.label}>{translate("UPC")}</Typography>
                    {product.detail?.upcCode}
                </Typography>
            ) : null}
            {fields.showCustomerPartNumber
                ? createWidgetElement("ProductDetails/ProductDetailsCustomerPartNumber", {
                      fields: {},
                  })
                : null}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsPartNumbers),
    definition: {
        group: "Xngage  Custom" as any,
        displayName: "Product Details Part Numbers",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showBrand,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: true,
            },
            {
                name: fields.showMFGPartNumber,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: true,
            },
            {
                name: fields.showWernerPartNumber,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: true,
            },
            {
                name: fields.showUPC,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: true,
            },
            {
                name: fields.showCustomerPartNumber,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: true,
            },
        ],
    },
};

export default widgetModule;
